import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { apiErr } from "../lib/utils";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { CalendarRange, Plus, Save, Users, Shield } from "lucide-react";
import { toast } from "sonner";

const cellKey = (code, p) => `${code}|${p}`;

export function BackfillKpiDialog({ open, onOpenChange, onSaved }) {
  const [periods, setPeriods] = useState(["2022", "2023", "2024"]);
  const [matrix, setMatrix] = useState(null);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);

  const loadMatrix = useCallback((plist) => {
    const q = plist && plist.length ? `?periods=${plist.join(",")}` : "";
    api.get(`/esg-kpis/matrix${q}`)
      .then((r) => { setMatrix(r.data); setPeriods(r.data.periods); })
      .catch((e) => toast.error(apiErr(e)));
  }, []);

  useEffect(() => {
    if (!open) return;
    setMatrix(null);
    setEdits({});
    loadMatrix();
  }, [open, loadMatrix]);

  const addEarlier = () => {
    const years = periods.map((p) => parseInt(p, 10)).filter((n) => !Number.isNaN(n));
    const earliest = Math.min(...years);
    if (!Number.isFinite(earliest) || periods.length >= 8) return;
    loadMatrix([String(earliest - 1), ...periods]);
  };

  const baseValue = (code, p) => {
    const row = [...(matrix?.social || []), ...(matrix?.governance || [])].find((r) => r.code === code);
    const v = row?.values?.[p];
    return v === null || v === undefined ? "" : String(v);
  };
  const cellValue = (code, p) => {
    const k = cellKey(code, p);
    return k in edits ? edits[k] : baseValue(code, p);
  };
  const setCell = (code, p, val) => setEdits((e) => ({ ...e, [cellKey(code, p)]: val }));

  const changedCount = Object.entries(edits).filter(([k, val]) => {
    const [code, p] = k.split("|");
    return String(val) !== baseValue(code, p);
  }).length;

  const save = async () => {
    const entries = [];
    Object.entries(edits).forEach(([k, val]) => {
      if (val === "" || val === null || val === undefined) return;
      const num = parseFloat(val);
      if (Number.isNaN(num)) return;
      const [code, period] = k.split("|");
      if (String(val) === baseValue(code, period)) return;
      entries.push({ code, period, value: num });
    });
    if (!entries.length) { toast.info("No changes to save"); return; }
    setSaving(true);
    try {
      const r = await api.post("/esg-kpis/values/bulk", { entries });
      toast.success(`Backfilled ${r.data.upserted} value${r.data.upserted === 1 ? "" : "s"} across ${periods.length} periods`);
      onSaved && onSaved();
      onOpenChange(false);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  const renderSection = (label, Icon, rows) => (
    <>
      <tr className="bg-surface">
        <td colSpan={periods.length + 1} className="px-3 py-2 sticky left-0">
          <span className="inline-flex items-center gap-1.5 label-eyebrow"><Icon className="h-3.5 w-3.5" /> {label} ({rows.length})</span>
        </td>
      </tr>
      {rows.map((row) => (
        <tr key={row.code} className="border-t border-border" data-testid={`backfill-row-${row.code}`}>
          <td className="px-3 py-1.5 sticky left-0 bg-card min-w-[240px]">
            <div className="text-sm font-medium leading-tight">{row.name}</div>
            <div className="text-[10px] text-muted-foreground">{row.code} · {row.unit}{row.esrs ? ` · ESRS ${row.esrs}` : ""}</div>
          </td>
          {periods.map((p) => (
            <td key={p} className="px-1.5 py-1.5">
              <Input
                type="number" step="any" inputMode="decimal"
                value={cellValue(row.code, p)}
                onChange={(e) => setCell(row.code, p, e.target.value)}
                className="h-8 w-24 text-right font-mono text-xs"
                data-testid={`backfill-cell-${row.code}-${p}`}
              />
            </td>
          ))}
        </tr>
      ))}
    </>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="backfill-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><CalendarRange className="h-5 w-5 text-accent" /> Backfill KPI history</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground -mt-1">
          Enter historical values across periods to build multi-year trends. Empty cells are skipped; existing values are pre-filled and updated on save.
        </p>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <Button variant="outline" size="sm" onClick={addEarlier} disabled={periods.length >= 8} data-testid="backfill-add-year">
            <Plus className="h-3.5 w-3.5" /> Add earlier year
          </Button>
          <Badge variant="outline" className="text-[10px]" data-testid="backfill-changed-count">{changedCount} unsaved change{changedCount === 1 ? "" : "s"}</Badge>
        </div>

        <div className="flex-1 overflow-auto card-flat">
          {!matrix ? (
            <div className="py-12 text-center text-muted-foreground text-sm">Loading grid…</div>
          ) : (
            <table className="w-full border-collapse text-sm">
              <thead className="sticky top-0 z-10 bg-card">
                <tr className="border-b border-border">
                  <th className="px-3 py-2 text-left sticky left-0 bg-card label-eyebrow text-[10px]">KPI</th>
                  {periods.map((p) => (
                    <th key={p} className="px-1.5 py-2 text-right label-eyebrow text-[10px] w-24">{p}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {renderSection("Social", Users, matrix.social)}
                {renderSection("Governance", Shield, matrix.governance)}
              </tbody>
            </table>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="backfill-cancel">Cancel</Button>
          <Button variant="primary" onClick={save} disabled={saving || changedCount === 0} data-testid="backfill-save">
            <Save className="h-4 w-4" /> {saving ? "Saving…" : `Save ${changedCount || ""} value${changedCount === 1 ? "" : "s"}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
